import { ChangeDetectionStrategy, Component, computed, EventEmitter, inject, input, Output } from '@angular/core';
import { NgClass } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  TooltipModule,
  RequiredInputToothConfiguration,
  RequiredInputSurfaceConfiguration,
  RequiredInputRangeConfiguration,
  RequiredInputQuadrantConfiguration,
  RequiredInputArchConfiguration,
  StatusBoxComponent,
} from '@libs/ui-lib';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { ProgressNoteStatus } from '@ffd/api-client';
import { SelectStatusComponent } from '../select-status';
import { AddNewTreatmentData } from '../add-treatment-modal.vm';

interface Tag {
  title: string;
  isSurface?: boolean;
}

@Component({
  selector: 'lib-treatment-tray-card',
  templateUrl: './treatment-tray-card.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SelectStatusComponent, NgClass, TranslatePipe, TooltipModule, SvgIconComponent, StatusBoxComponent],
})
export class TreatmentTrayCardComponent {
  public treatment = input.required<AddNewTreatmentData>();

  @Output() public treatmentRemoved = new EventEmitter<void>();
  @Output() public treatmentUpdated = new EventEmitter<AddNewTreatmentData>();
  @Output() public requiredInputEditRequested = new EventEmitter<void>();
  @Output() public surfaceEditRequested = new EventEmitter<number>();

  private readonly translateService = inject(TranslateService);
  public readonly ProgressNoteStatus = ProgressNoteStatus;

  public status = computed(() => this.treatment().status);
  public isCondition = computed(() => this.treatment().isCondition);
  public dentalCode = computed(() => this.treatment().dentalCode);
  public description = computed(() => this.treatment().description);
  public position = computed(() => (this.treatment().requiredInputData as RequiredInputToothConfiguration)?.position);
  public surfaces = computed(() => (this.treatment().requiredInputData as RequiredInputSurfaceConfiguration)?.surfaces);
  public range = computed(() => (this.treatment().requiredInputData as RequiredInputRangeConfiguration)?.range);
  public quadrants = computed(() => (this.treatment().requiredInputData as RequiredInputQuadrantConfiguration)?.quadrants);
  public arches = computed(() => (this.treatment().requiredInputData as RequiredInputArchConfiguration)?.arches);
  public isFullMouth = computed(() => !this.treatment().requiredInputData);

  public tagClass = 'typo-h5-semibold h-[22px] rounded-[8px] bg-background-tag px-2 py-[3px] text-primary-blue hover:cursor-pointer';

  public treatmentTags = computed(() => {
    const tags: Tag[] = [];

    if (this.position()) {
      tags.push({ title: this.position()!.toString() });
    }

    if (this.surfaces()) {
      tags.push({ title: this.surfaces()!.join(''), isSurface: true });
    }

    if (this.range()) {
      tags.push({ title: this.range()! });
    }

    if (this.quadrants()) {
      this.quadrants()!.forEach((quadrant) => {
        const translationKey = `charting_add_treatment_quadrant_${quadrant.replace('-', '_')}`;
        tags.push({ title: this.translateService.instant(translationKey) });
      });
    }

    if (this.arches()) {
      this.arches()!.forEach((arch) => {
        const translationKey = `charting_add_treatment_arch_${arch.replace('-', '_')}`;
        tags.push({ title: this.translateService.instant(translationKey) });
      });
    }

    return tags;
  });

  public onRemoveTreatment(): void {
    this.treatmentRemoved.emit();
  }

  public onTreatmentStatusChange(newStatus: ProgressNoteStatus): void {
    const updatedTreatment: AddNewTreatmentData = {
      ...this.treatment(),
      status: newStatus,
    };
    this.treatmentUpdated.emit(updatedTreatment);
  }

  public onEditTreatment(tag: Tag): void {
    if (tag.isSurface) {
      this.surfaceEditRequested.emit(this.position());
    } else {
      this.requiredInputEditRequested.emit();
    }
  }
}
